import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Upload, X, Play } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";

const NETWORKS = [
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "tiktok", label: "TikTok" },
];
const TYPES = [
  { value: "feed", label: "Feed" },
  { value: "story", label: "Story" },
  { value: "reels", label: "Reels" },
];

export default function CreatePostDialog({ clientId, year, month, defaultDay, onCreated }) {
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    day: defaultDay || 1,
    post_type: "feed",
    social_networks: ["instagram"],
    copy: "",
    scheduled_time: "",
    images: [],
  });

  const reset = () => setForm({ day: defaultDay || 1, post_type: "feed", social_networks: ["instagram"], copy: "", scheduled_time: "", images: [] });

  const toggleNetwork = (value) => {
    setForm((s) => ({
      ...s,
      social_networks: s.social_networks.includes(value)
        ? s.social_networks.filter((x) => x !== value)
        : [...s.social_networks, value],
    }));
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(
          `/upload?client_id=${encodeURIComponent(clientId)}&year=${year}&month=${month}&kind=post`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        uploaded.push({ path: data.path, filename: data.filename, media_type: data.media_type || "image" });
      } catch (err) {
        toast.error(`Erro a carregar ${f.name}`);
      }
    }
    setForm((s) => ({ ...s, images: [...s.images, ...uploaded] }));
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImage = (idx) => {
    setForm((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.social_networks.length === 0) {
      toast.error("Selecione pelo menos uma rede social");
      return;
    }
    setLoading(true);
    try {
      await api.post("/posts", {
        client_id: clientId,
        year, month,
        day: Number(form.day),
        post_type: form.post_type,
        social_networks: form.social_networks,
        copy: form.copy,
        scheduled_time: form.scheduled_time || null,
        images: form.images,
      });
      toast.success("Post criado");
      setOpen(false);
      reset();
      onCreated && onCreated();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao criar post");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          className="rounded-full bg-[#C07C5B] hover:bg-[#A86847] text-white h-11 px-5 text-sm tracking-wide"
          data-testid="new-post-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Novo post
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#F9F6F0] border border-[#EAE3DB] rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#1A1412]">
            Novo post — {String(month).padStart(2, "0")}/{year}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#3B2B23]">Dia</Label>
              <Input
                type="number" min="1" max="31" required
                value={form.day}
                onChange={(e) => setForm({ ...form, day: e.target.value })}
                className="mt-2 h-11 bg-white border-[#EAE3DB]"
                data-testid="post-day-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#3B2B23]">Hora</Label>
              <Input
                type="time"
                value={form.scheduled_time}
                onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })}
                className="mt-2 h-11 bg-white border-[#EAE3DB]"
                data-testid="post-time-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#3B2B23]">Tipo</Label>
              <Select value={form.post_type} onValueChange={(v) => setForm({ ...form, post_type: v })}>
                <SelectTrigger className="mt-2 h-11 bg-white border-[#EAE3DB]" data-testid="post-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-[#3B2B23]">Redes sociais</Label>
            <p className="mt-1 text-[11px] text-[#5C4A43] font-light">Posso selecionar várias.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {NETWORKS.map((n) => {
                const active = form.social_networks.includes(n.value);
                return (
                  <button
                    key={n.value}
                    type="button"
                    onClick={() => toggleNetwork(n.value)}
                    className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition-all ${
                      active
                        ? "bg-[#3B2B23] text-[#F9F6F0] border-[#3B2B23]"
                        : "bg-white text-[#3B2B23] border-[#EAE3DB] hover:border-[#8B5E3C]"
                    }`}
                    data-testid={`post-network-toggle-${n.value}`}
                  >
                    {n.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-[#3B2B23]">Copy</Label>
            <Textarea
              rows={6}
              value={form.copy}
              onChange={(e) => setForm({ ...form, copy: e.target.value })}
              placeholder="Escreva o copy do post…"
              className="mt-2 bg-white border-[#EAE3DB] resize-none"
              data-testid="post-copy-textarea"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-[#3B2B23]">Ficheiros (imagens ou vídeo)</Label>
            <div className="mt-2 flex flex-wrap gap-3">
              {form.images.map((img, idx) => (
                <div key={idx} className="relative w-24 h-24 rounded-xl overflow-hidden border border-[#EAE3DB] bg-white">
                  {img.media_type === "video" ? (
                    <div className="w-full h-full bg-[#3B2B23] flex items-center justify-center">
                      <Play className="w-6 h-6 text-[#F9F6F0]" />
                    </div>
                  ) : (
                    <AuthImage path={img.path} className="w-full h-full object-cover" />
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute top-1 right-1 bg-[#3B2B23] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                    data-testid={`remove-image-${idx}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-24 h-24 rounded-xl border-2 border-dashed border-[#8B5E3C] text-[#5C4A43] flex flex-col items-center justify-center text-xs hover:bg-[#EAE3DB]/40 transition-colors"
                data-testid="upload-image-button"
              >
                <Upload className="w-5 h-5 mb-1" />
                {uploading ? "A enviar…" : "Adicionar"}
              </button>
              <input
                ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={handleFiles}
              />
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button" variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB]"
              data-testid="post-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit" disabled={loading}
              className="rounded-full bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0]"
              data-testid="post-save-button"
            >
              {loading ? "A guardar…" : "Guardar post"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
